/**
 * Thin stat bar (HP / resource / XP) with "cur/max" text above it.
 *
 * Instant on change: the fill is a `transform: scaleX()` written the same frame the value
 * changes (no CSS transition). Smooth: a lighter "ghost" trail holds the old value briefly and
 * then drains toward the new one (damage taken reads at a glance). Both layers are
 * compositor-only transforms indexed from an interned table (tokens.ts SCALE_X); the text is
 * rebuilt only when the rounded numbers change. No layout reads, no per-frame allocations.
 */
import { el } from '../dom';
import { shortNumber } from '../format';
import { BAR_STEPS, SCALE_X, barIndex } from './tokens';

export type StatTextMode = 'cur/max' | 'cur' | 'pct' | 'none';

export interface StatBarOptions {
  /** Fill colour or gradient (sets --hk-stat-fill). Default: HP red. */
  color?: string;
  /** 'rtl' anchors the fill on the right (HP bar extending left from the crest). */
  dir?: 'ltr' | 'rtl';
  text?: StatTextMode;
  textAlign?: 'left' | 'right' | 'center';
  /** 'full' = 47,904 / 'short' = 47.9K. */
  numbers?: 'full' | 'short';
  /** Thin (4 px) or normal (7 px). */
  height?: 'sm' | 'md';
  /** Damage trail (default true). */
  ghost?: boolean;
  /** Overlay after the fill (absorb shields). */
  extra?: boolean;
  /** Draw dividers for N segments (combo points / holy power). */
  segments?: number;
  className?: string;
}

/** Ghost trail: wait this long after a drop, then drain at this rate (fraction of the bar per second). */
const GHOST_HOLD_MS = 220;
const GHOST_RATE = 1.6;

export class StatBar {
  readonly root: HTMLElement;
  readonly track: HTMLElement;
  private readonly fill: HTMLElement;
  private readonly ghost: HTMLElement | null;
  private readonly extra: HTMLElement | null;
  private readonly text: HTMLElement | null;
  private readonly mode: StatTextMode;
  private readonly short: boolean;
  private readonly rtl: boolean;
  private fillIdx = -1;
  private ghostIdx = -1;
  /** Ghost position (0..BAR_STEPS) and the time it may start draining. */
  private ghostPos = 0;
  private ghostHoldUntil = 0;
  private lastTick = -1;
  private extraKey = -1;
  private curShown = NaN;
  private maxShown = NaN;

  constructor(parent: HTMLElement | null, opts: StatBarOptions = {}) {
    const dir = opts.dir ?? 'ltr';
    this.mode = opts.text ?? 'cur/max';
    this.short = opts.numbers === 'short';
    this.rtl = dir === 'rtl';
    this.root = el('div', `hk-stat ${dir} ${opts.height ?? 'md'}${opts.className ? ` ${opts.className}` : ''}`);
    this.text = this.mode === 'none' ? null : el('div', `hk-stat-text hk-num ${opts.textAlign ?? (dir === 'rtl' ? 'right' : 'left')}`, this.root);
    this.track = el('div', 'hk-stat-track', this.root);
    this.ghost = opts.ghost === false ? null : el('div', 'hk-stat-ghost', this.track);
    this.fill = el('div', 'hk-stat-fill', this.track);
    this.extra = opts.extra ? el('div', 'hk-stat-extra', this.track) : null;
    el('div', 'hk-stat-gloss', this.track);
    if (opts.color) this.setColor(opts.color);
    if (opts.segments) this.setSegments(opts.segments);
    parent?.appendChild(this.root);
  }

  setColor(color: string): void {
    if (this.root.style.getPropertyValue('--hk-stat-fill') !== color) this.root.style.setProperty('--hk-stat-fill', color);
  }

  /** Segment dividers (0 / 1 = none). Call on change only. */
  setSegments(n: number): void {
    this.root.classList.toggle('segmented', n > 1);
    this.root.style.setProperty('--hk-segs', String(Math.max(1, n)));
  }

  /**
   * Draw a frame: `cur / max` (+ optional `extra` amount for the overlay, e.g. absorb), at
   * real time `now` (performance.now; drives the ghost trail).
   */
  update(cur: number, max: number, now: number, extra = 0): void {
    const m = max > 0 ? max : 1;
    const fi = barIndex(cur / m);
    if (fi !== this.fillIdx) {
      if (fi < this.fillIdx && this.ghost) {
        // A drop: hold the ghost where the fill was (or keep an older, higher ghost).
        this.ghostPos = Math.max(this.ghostPos, this.fillIdx);
        this.ghostHoldUntil = now + GHOST_HOLD_MS;
      }
      this.fillIdx = fi;
      this.fill.style.transform = SCALE_X[fi]!;
    }
    if (this.ghost) this.tickGhost(now);
    if (this.extra) this.drawExtra(fi, extra > 0 ? barIndex(extra / m) : 0);
    if (this.text && (cur !== this.curShown || max !== this.maxShown)) this.drawText(cur, max);
  }

  /** Jump straight to a value (unit swap): no ghost trail. */
  snap(cur: number, max: number, now: number, extra = 0): void {
    this.fillIdx = -1;
    this.ghostPos = 0;
    this.update(cur, max, now, extra);
    this.ghostPos = this.fillIdx;
    if (this.ghost) this.tickGhost(now);
  }

  private tickGhost(now: number): void {
    const dt = this.lastTick < 0 ? 0 : Math.min(100, Math.max(0, now - this.lastTick));
    this.lastTick = now;
    if (this.ghostPos <= this.fillIdx) this.ghostPos = this.fillIdx;
    else if (now >= this.ghostHoldUntil) this.ghostPos = Math.max(this.fillIdx, this.ghostPos - (GHOST_RATE * BAR_STEPS * dt) / 1000);
    const gi = Math.round(this.ghostPos);
    if (gi !== this.ghostIdx) {
      this.ghostIdx = gi;
      this.ghost!.style.transform = SCALE_X[gi]!;
    }
  }

  private drawExtra(fi: number, ei: number): void {
    const width = Math.min(BAR_STEPS - fi, ei);
    const key = fi * (BAR_STEPS + 1) + width;
    if (key === this.extraKey) return;
    this.extraKey = key;
    // The overlay starts where the fill ends: translate by the fill, scale by the extra (built on change only).
    // rtl: the overlay's transform-origin is the right edge, so it moves left.
    this.extra!.style.transform = width > 0 ? `translateX(${this.rtl ? -fi / 10 : fi / 10}%) scaleX(${width / BAR_STEPS})` : SCALE_X[0]!;
  }

  private drawText(cur: number, max: number): void {
    this.curShown = cur;
    this.maxShown = max;
    const c = Math.max(0, Math.round(cur));
    const fmt = this.short ? shortNumber : groupNumber;
    let s: string;
    switch (this.mode) {
      case 'cur':
        s = fmt(c);
        break;
      case 'pct':
        s = `${Math.round((c / (max > 0 ? max : 1)) * 100)}%`;
        break;
      default:
        s = `${fmt(c)}/${fmt(Math.round(max))}`;
    }
    this.text!.textContent = s;
  }
}

/** 47904 → "47,904". */
export function groupNumber(n: number): string {
  const s = String(Math.round(Math.abs(n)));
  const out = s.length > 3 ? s.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : s;
  return n < 0 ? `-${out}` : out;
}
